import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { Portal } from '../../utils/Portal';
import { cn } from '../../utils/cn';

const SIDES = {
  right:  { container: 'top-0 right-0 h-full', size: 'w-[420px] max-w-[100vw]', anim: 'animate-slide-down [&]:[animation-name:none] [animation-name:slide-in-right]' },
  left:   { container: 'top-0 left-0 h-full',  size: 'w-[320px]', anim: '' },
  top:    { container: 'top-0 left-0 right-0', size: 'h-[40vh] w-full', anim: 'animate-slide-down' },
  bottom: { container: 'bottom-0 left-0 right-0', size: 'h-[40vh] w-full', anim: 'animate-slide-up' },
};

export function Drawer({ isOpen, onClose, side = 'right', title, description, className, children, footer }) {
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const conf = SIDES[side];

  return (
    <Portal>
      <div className="fixed inset-0 z-drawer">
        <div aria-hidden onClick={onClose} className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] animate-fade-in" />
        <aside
          role="dialog"
          aria-modal="true"
          aria-label={title || 'Panneau latéral'}
          className={cn('absolute bg-surface border border-border shadow-xl flex flex-col', conf.container, conf.size, side === 'right' && 'border-l border-y-0 border-r-0', side === 'left' && 'border-r border-y-0 border-l-0', className)}
          style={{ animation: side === 'right' ? 'slide-in-right 200ms cubic-bezier(0.2,0,0,1.1)' : undefined }}
        >
          <header className="flex items-start justify-between px-5 py-4 border-b border-border">
            <div>
              {title && <h2 className="text-lg font-bold tracking-tight">{title}</h2>}
              {description && <p className="text-sm text-mute mt-1 leading-relaxed">{description}</p>}
            </div>
            <button type="button" onClick={onClose} aria-label="Fermer" className="p-1.5 rounded-md text-mute hover:text-text hover:bg-bg-2 -mr-1">
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="px-5 py-3 border-t border-border bg-bg-2/50 flex items-center justify-end gap-2">{footer}</footer>}
        </aside>
        <style>{`
          @keyframes slide-in-right {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}</style>
      </div>
    </Portal>
  );
}

export default Drawer;
